import { Component, ElementRef, Input, Signal } from '@angular/core'
import {
    BookSessionPreferences,
    SESSION_DURATION_25_MINS,
    SESSION_DURATION_50_MINS,
    SESSION_DURATION_75_MINS,
    User,
} from '@app/core/_models'
import { CommonModule } from '@angular/common'
import { BookSessionInfo, BookSessionsService, SelectedSessionService } from '@app/core/_services'
import { WelcomeChecklistService } from '@app/core/_services/welcome-checklist.service'
import { Subject, take, takeUntil } from 'rxjs'
import { DialogService } from '@app/core/_services/dialog.service'
import { BookingSource } from '@app/core/_services/analytics.service'
import { ErrorHandlerService } from '@app/core/_services/error-handler.service'
import { UserService } from '@app/core/_services/user.service'
import { RewardsReferralService } from '@app/core/_services/rewards-referral.service'
import { ButtonModule } from 'primeng/button'
import { DstBoundaryService } from '@app/core/_services/dst-service'
import { MODAL_VERIFY_EMAIL_BOOKING } from '@app/shared-dialogs/email-verify/email-verify-dialog.component'

// NOTE: Defining again here since importing this component in two
// different routes /dashboard and /sessions is causing a circular dependency
// on the calendar_day component. TODO: Move all constants to a shared file?
const NUM_TIMESLOTS_PER_DAY = 24 * 4 + 3
const TIMESLOT_HEIGHT = 48 // Each 15minutes slot is 48px high

// Define the interface for the session-related dimensions
interface SelectedSessionDimensions {
    tileHeight: number
}

@Component({
    selector: 'fm-selected-session-tile',
    imports: [CommonModule, ButtonModule],
    templateUrl: './selected-session-tile.component.html',
    styleUrl: './selected-session-tile.component.scss',
})
export class SelectedSessionTileComponent {
    @Input() sessionTime: number
    @Input() sessionDuration: number = SESSION_DURATION_25_MINS
    @Input() slotNumber: number | null = null
    @Input() booking: boolean = false
    @Input() timezone: string

    private ngUnsubscribe: Subject<any> = new Subject<any>()
    private currentUser: User = new User()
    public tileHeight: number = 0

    private sessionDimensionsMap: { [key: number]: SelectedSessionDimensions } = {
        [SESSION_DURATION_25_MINS]: {
            tileHeight: TIMESLOT_HEIGHT * 2,
        },
        [SESSION_DURATION_50_MINS]: {
            tileHeight: TIMESLOT_HEIGHT * 4,
        },
        [SESSION_DURATION_75_MINS]: {
            tileHeight: TIMESLOT_HEIGHT * 6,
        },
    }

    private isWelcomeChecklistActive: Signal<boolean> =
        this.welcomeChecklistService.isWelcomeChecklistActive

    constructor(
        private elementRef: ElementRef,
        private rewardsReferralService: RewardsReferralService,
        private userService: UserService,
        private errorHandlerService: ErrorHandlerService,
        private dialogService: DialogService,
        private welcomeChecklistService: WelcomeChecklistService,
        private bs: BookSessionsService,
        private selectedSessionService: SelectedSessionService,
        private dstBoundaryService: DstBoundaryService,
    ) {}

    ngOnInit() {
        this.userService.currentUser
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((currentUser: User) => {
                this.currentUser = currentUser
            })
    }

    ngOnChanges() {
        if (this.sessionDuration) {
            const dstData = this.dstBoundaryService.intersectsSpringForward(
                this.timezone,
                this.sessionTime,
                this.sessionTime + this.sessionDuration,
            )

            let tileHeight

            if (this.sessionDuration === SESSION_DURATION_75_MINS && this.slotNumber > 93) {
                tileHeight = this.sessionDimensionsMap[SESSION_DURATION_50_MINS].tileHeight
            } else {
                tileHeight = this.sessionDimensionsMap[this.sessionDuration].tileHeight
            }

            if (dstData.intersects) {
                tileHeight += TIMESLOT_HEIGHT * (dstData.shiftMinutes / 15)

                if (tileHeight / TIMESLOT_HEIGHT + this.slotNumber >= NUM_TIMESLOTS_PER_DAY) {
                    tileHeight = (NUM_TIMESLOTS_PER_DAY - this.slotNumber - 1) * TIMESLOT_HEIGHT
                }
            }

            this.tileHeight = tileHeight
        }
    }

    onClickClearSelectedSession(event: Event) {
        event.stopPropagation()
        this.selectedSessionService.deleteSelectedSession(this.sessionTime)
    }

    onClickBookSession(event: Event) {
        if (this.booking) {
            return
        }

        event.stopPropagation()
        const selectedSession = this.selectedSessionService.getSelectedSessionInfoByTime(
            this.sessionTime,
        )
        const preferences: BookSessionPreferences = {
            favorites: { value: selectedSession.sessionPreferenceFavorites },
            quietMode: { value: selectedSession.quietMode },
        }

        let bookSessionInfo: BookSessionInfo = {
            sessionTime: this.sessionTime,
            userAvailabilityCode: '',
            duration: this.sessionDuration,
            userId: '',
            title: '',
            preferences,
            activityType: selectedSession.activityType,
        }

        this.bs
            .bookSessions([bookSessionInfo], this.currentUser.timeZone, BookingSource.CalendarTile)
            .pipe(take(1))
            .subscribe(
                (res) => {
                    let parsedRes = this.bs.parseBookResponse(res)

                    if (parsedRes['numSuccess'] !== 0) {
                        this.dialogService.openConfirmTransaction(
                            parsedRes['numSuccess'] === 1
                                ? 'Session booked!'
                                : parsedRes['numSuccess'] + ' sessions booked!',
                            3000,
                            { desktopOnly: true },
                        )
                    }

                    if (this.currentUser.emailVerified === false) {
                        this.dialogService.openEmailVerificationDialog(
                            MODAL_VERIFY_EMAIL_BOOKING,
                            () => {
                                this.processParsedResult(parsedRes)
                            },
                        )
                    } else {
                        this.processParsedResult(parsedRes)
                    }
                },
                (error) => {
                    this.errorHandlerService.handleErrorResponse(error)
                },
            )
    }

    processParsedResult(parsedRes) {
        if (parsedRes['planLimitReached'] === true) {
            this.dialogService.openPlanLimitReachedDialog()
        } else if (
            !this.isWelcomeChecklistActive() &&
            !this.currentUser.properties.viewedSegmentationSurveyModal &&
            this.currentUser.compSessions >= 3
        ) {
            this.dialogService.openSegmentationSurveyModal()
        } else if (
            !this.isWelcomeChecklistActive() &&
            this.rewardsReferralService.shouldSeeReferralPrompt(this.currentUser)
        ) {
            this.dialogService.openReferralPromptModal()
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
